import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdCalendarToday,
  MdAccessTime,
  MdPeople,
  MdEdit,
  MdDevices,
  MdEventBusy,
} from "react-icons/md";
import attendanceService from "services/attendanceService";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import Loader from "components/loader/Loader";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { formatDate, formatNumber } from "utils/format";
import { DatePicker } from "components/fields";

const STATUS_TONES = {
  completed: "success",
  upcoming: "info",
  cancelled: "danger",
};

const METRIC_FIELDS = [
  { key: "attendance", label: "Attendance" },
  { key: "kids", label: "Kids" },
  { key: "salvations", label: "Salvations" },
  { key: "volunteers", label: "Volunteers" },
];

const ServiceCard = ({ service, showMetrics, onOpen }) => (
  <Card padding="md" className="gap-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <h3 className="text-heading">{service.serviceName}</h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-fg-muted">
          <span className="flex items-center gap-1">
            <MdCalendarToday aria-hidden="true" />
            {formatDate(service.serviceDate)}
          </span>
          <span className="flex items-center gap-1">
            <MdAccessTime aria-hidden="true" />
            {attendanceService.formatTime(service.serviceTime)}
          </span>
        </div>
      </div>
      {service.status && (
        <Badge tone={STATUS_TONES[service.status] || "neutral"} className="capitalize">
          {service.status}
        </Badge>
      )}
    </div>

    {service.campus && (
      <p className="text-sm text-fg-muted">
        <span className="font-medium text-fg">Campus:</span> {service.campus}
      </p>
    )}

    {showMetrics &&
      (service.metrics ? (
        <dl className="grid grid-cols-2 gap-3 rounded-control bg-surface-raised p-4">
          {METRIC_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <dt className="text-xs font-medium uppercase text-fg-muted">{label}</dt>
              <dd className="text-2xl font-bold text-fg">
                {formatNumber(service.metrics[key] || 0)}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="rounded-control bg-surface-raised p-4 text-center text-sm text-fg-muted">
          No metrics entered yet
        </p>
      ))}

    <Button
      fullWidth
      className="mt-auto"
      leftIcon={service.metrics && showMetrics ? <MdEdit /> : <MdPeople />}
      onClick={() => onOpen(service.id)}
    >
      {showMetrics ? `${service.metrics ? "Edit" : "Enter"} Attendance` : "View Service"}
    </Button>
  </Card>
);

const CardGridSkeleton = ({ count = 3 }) => (
  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: count }, (_, i) => (
      <Skeleton key={i} className="h-64 rounded-card" />
    ))}
  </div>
);

const ServiceList = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [services, setServices] = useState([]);
  const [upcomingServices, setUpcomingServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [hasDevices, setHasDevices] = useState(null);
  const [checkingDevices, setCheckingDevices] = useState(true);

  const checkDevices = useCallback(async () => {
    setCheckingDevices(true);
    try {
      const response = await attendanceService.checkHasDevices();
      setHasDevices(response.hasDevices || false);
    } catch {
      // If the check fails, assume no devices so web entry stays available.
      setHasDevices(false);
    } finally {
      setCheckingDevices(false);
    }
  }, []);

  useEffect(() => {
    track(EVENTS.ATTENDANCE_PAGE_VIEWED);
    checkDevices();
  }, [checkDevices]);

  const loadUpcomingServices = useCallback(async () => {
    setLoadingUpcoming(true);
    try {
      // The next 7 days, starting the day after the selected date.
      const today = new Date(selectedDate);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const response = await attendanceService.getServiceInstances({
        startDate: tomorrow.toISOString().split("T")[0],
        endDate: nextWeek.toISOString().split("T")[0],
        includeMetrics: true,
      });
      setUpcomingServices(response || []);
    } catch {
      // Upcoming services are a suggestion; the empty state already covers the day.
      setUpcomingServices([]);
    } finally {
      setLoadingUpcoming(false);
    }
  }, [selectedDate]);

  const loadServices = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await attendanceService.getServiceInstances({
        date: selectedDate,
        includeMetrics: true,
      });
      setServices(response || []);

      if (!response || response.length === 0) {
        loadUpcomingServices();
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, loadUpcomingServices]);

  useEffect(() => {
    if (hasDevices === false && selectedDate) {
      loadServices();
    }
  }, [selectedDate, hasDevices, loadServices]);

  const handleEnterMetrics = (serviceId) => {
    navigate(`/admin/attendance/entry/${serviceId}`);
  };

  const renderContent = () => {
    if (checkingDevices) return <Loader />;

    if (hasDevices) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdDevices}
            title="Devices Detected"
            description="Your organization has physical devices assigned for attendance tracking. Please use your assigned devices to record attendance. Web-based attendance entry is only available for organizations without physical devices."
            action={
              <Button onClick={() => navigate("/admin/settings?tab=general")}>View Settings</Button>
            }
          />
        </Card>
      );
    }

    if (loading) return <CardGridSkeleton />;

    if (loadError) {
      return (
        <Card padding="md">
          <ErrorState
            title="Couldn't Load Services"
            message={getApiErrorMessage(loadError, "Could not load services for this date")}
            onRetry={loadServices}
          />
        </Card>
      );
    }

    if (services.length === 0) {
      return (
        <>
          <Card padding="md">
            <EmptyState
              variant="filtered"
              icon={MdEventBusy}
              title="No Services on This Date"
              description="Try selecting a different date or generate service instances."
            />
          </Card>

          {loadingUpcoming ? (
            <CardGridSkeleton />
          ) : (
            upcomingServices.length > 0 && (
              <section className="flex flex-col gap-4">
                <CardHeader title="Upcoming Services (Next 7 Days)" />
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {upcomingServices.map((service) => (
                    <ServiceCard key={service.id} service={service} onOpen={handleEnterMetrics} />
                  ))}
                </div>
              </section>
            )
          )}
        </>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {services.map((service) => (
          <ServiceCard key={service.id} service={service} showMetrics onOpen={handleEnterMetrics} />
        ))}
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Services"
        description="View and manage service attendance metrics"
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <DatePicker
              label="Service Date"
              valueFormat="YYYY-MM-DD"
              value={selectedDate}
              onChange={setSelectedDate}
              className="w-full sm:w-52"
            />
            <Button variant="secondary" onClick={loadServices}>
              Load Services
            </Button>
          </div>
        }
      />

      {renderContent()}
    </PageContainer>
  );
};

export default ServiceList;
